import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Button, type ButtonProps } from '../ui/button'
import { cn } from '../../lib/utils'

// One canonical back / forward wizard button so every step flow renders the
// same chevron. Always ChevronLeft for "back" / ChevronRight for "forward" —
// a global CSS rule (packages/ui/src/styles/fluid.css) auto-mirrors every
// lucide chevron/arrow under dir="rtl" via `transform: scaleX(-1)`, so RTL
// flipping is handled ONCE, centrally. Do NOT also swap the icon component
// here based on isRTL — that was the actual bug (reported repeatedly as "the
// back arrow points the wrong way in Arabic"): the CSS rule mirrored the
// already-manually-flipped icon a second time, landing back on the wrong
// direction. Picking the icon by direction alone and trusting the CSS layer
// to mirror it is what makes preview and live match in both directions.
//
//   direction="back"     LTR ◄   RTL ► (CSS-mirrored)
//   direction="forward"  LTR ►   RTL ◄ (CSS-mirrored)
//
// The chevron sits on the leading edge for "back" and the trailing edge for
// "forward", matching how the browser lays the button contents out under
// `dir="rtl"`.
export interface WizardNavButtonProps extends Omit<ButtonProps, 'children'> {
  direction: 'back' | 'forward'
  label: string
  /** Show a spinner before the label (e.g. while verifying / saving). */
  loading?: boolean
  /** Hide the chevron (e.g. the final "Finish" step has no next arrow). */
  hideChevron?: boolean
  /** Collapse the label to icon-only on narrow screens (`hidden sm:inline`). */
  hideLabelOnMobile?: boolean
}

export function WizardNavButton({
  direction, label, loading = false, hideChevron = false, hideLabelOnMobile = false,
  className, disabled, ...props
}: WizardNavButtonProps) {
  const Chevron = direction === 'back' ? ChevronLeft : ChevronRight
  const chevron = hideChevron ? null : <Chevron className="w-4 h-4" />

  return (
    <Button className={cn('gap-1.5', className)} disabled={disabled || loading} {...props}>
      {direction === 'back' && chevron}
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      <span className={cn(hideLabelOnMobile && 'hidden sm:inline')}>{label}</span>
      {direction === 'forward' && chevron}
    </Button>
  )
}
